/**
 * audioUtils.js — KDS chimes via Web Audio (side-effecting, but isolated here).
 */

// Shared AudioContext so a single user gesture can "unlock" audio for the
// whole session (browsers block autoplay until the context is resumed once).
let _sharedAudioCtx = null;
function getAudioCtx() {
  try {
    if (!_sharedAudioCtx) _sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
    return _sharedAudioCtx;
  } catch { return null; }
}

/**
 * Resume the shared AudioContext from inside a user gesture (click/tap).
 * Call this when the user enables alerts so later programmatic chimes are
 * allowed to play. Returns true if a context is available.
 */
export function unlockKdsAudio() {
  const ctx = getAudioCtx();
  if (!ctx) return false;
  try { if (ctx.state === 'suspended') ctx.resume(); } catch {}
  return true;
}

/**
 * Distinct "new order" chime — a friendly rising two-note ding-dong.
 * Deliberately different from playDelayAlert()'s descending alarm so staff can
 * tell a brand-new order apart from a late-order warning (KDS best practice).
 */
export function playNewOrderChime() {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') { try { ctx.resume(); } catch {} }
    // Rising notes: C6 → E6 → G6 (pleasant arpeggio up)
    [[1047, 0, 0.16], [1319, 0.14, 0.16], [1568, 0.28, 0.30]].forEach(([freq, startAt, dur]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + startAt);
      gain.gain.setValueAtTime(0, ctx.currentTime + startAt);
      gain.gain.linearRampToValueAtTime(0.5, ctx.currentTime + startAt + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startAt + dur);
      osc.start(ctx.currentTime + startAt);
      osc.stop(ctx.currentTime + startAt + dur);
    });
  } catch {}
}

export function playDelayAlert() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [[880, 0, 0.18], [660, 0.2, 0.18], [440, 0.42, 0.28]].forEach(([freq, startAt, dur]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + startAt);
      gain.gain.setValueAtTime(0, ctx.currentTime + startAt);
      gain.gain.linearRampToValueAtTime(0.55, ctx.currentTime + startAt + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startAt + dur);
      osc.start(ctx.currentTime + startAt);
      osc.stop(ctx.currentTime + startAt + dur);
    });
    setTimeout(() => ctx.close().catch(() => {}), 900);
  } catch {}
}
